import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { socialMediaApi } from '../api';
import { socialMediaQueryKeys } from '../api/queryKeys';
import type {
  SocialPlatform,
  SocialAnalyticsRange,
  PlatformAnalyticsStat,
  AudienceTimePoint,
  SocialEngagementByDay,
  PostEngagementRow,
} from '@/lib/api';

// ── Re-exported domain types (consumed by AnalyticsPage and chart components) ─

export type { SocialAnalyticsRange as DateRange };
export type { PlatformAnalyticsStat as PlatformStat };
export type { AudienceTimePoint as FollowerTimePoint };
export type { SocialEngagementByDay as EngagementByDay };
export type { PostEngagementRow as PostAnalyticsRow };

export interface FollowerDistribution {
  platform: SocialPlatform;
  count: number;
}

export interface SocialAnalyticsData {
  platforms: PlatformAnalyticsStat[];
  followerTimeSeries: AudienceTimePoint[];
  engagementByDay: SocialEngagementByDay[];
  followerDistribution: FollowerDistribution[];
  postAnalytics: PostEngagementRow[];
  postsHeatmap: Array<{ date: string; count: number }>;
  lastRefreshedAt: Date;
}

export interface UseSocialAnalyticsReturn extends SocialAnalyticsData {
  isLoading: boolean;
  error: string | null;
  dateRange: SocialAnalyticsRange;
  setDateRange: (range: SocialAnalyticsRange) => void;
  refresh: () => void;
}

// ── Placeholder data (used while loading or on first render) ──────────────────

function buildEmptyData(): SocialAnalyticsData {
  return {
    platforms: [],
    followerTimeSeries: [],
    engagementByDay: [],
    followerDistribution: [],
    postAnalytics: [],
    postsHeatmap: [],
    lastRefreshedAt: new Date(),
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useSocialAnalytics(holderId: string | null): UseSocialAnalyticsReturn {
  const [dateRange, setDateRange] = useState<SocialAnalyticsRange>('30d');

  const query = useQuery({
    queryKey: socialMediaQueryKeys.analytics(holderId ?? '', dateRange),
    queryFn: () => socialMediaApi.getSocialMediaAnalytics(holderId as string, { range: dateRange }),
    enabled: !!holderId,
    select: (result): SocialAnalyticsData => {
      // Build followerDistribution from the latest audience snapshot per platform.
      const followerDistribution: FollowerDistribution[] = result.platforms
        .filter((p) => p.audienceSize !== null)
        .map((p) => ({ platform: p.platform, count: p.audienceSize! }));

      return {
        platforms: result.platforms,
        followerTimeSeries: result.audienceTimeSeries,
        engagementByDay: result.engagementByDay,
        followerDistribution,
        postAnalytics: result.postEngagement,
        postsHeatmap: result.postsHeatmap,
        lastRefreshedAt: new Date(result.generatedAt),
      };
    },
  });

  // refetch is a stable reference (unlike the query result object).
  const { refetch } = query;
  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const empty = buildEmptyData();
  const data = query.data;

  return {
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    dateRange,
    setDateRange,
    refresh,
    platforms: data?.platforms ?? empty.platforms,
    followerTimeSeries: data?.followerTimeSeries ?? empty.followerTimeSeries,
    engagementByDay: data?.engagementByDay ?? empty.engagementByDay,
    followerDistribution: data?.followerDistribution ?? empty.followerDistribution,
    postAnalytics: data?.postAnalytics ?? empty.postAnalytics,
    postsHeatmap: data?.postsHeatmap ?? empty.postsHeatmap,
    lastRefreshedAt: data?.lastRefreshedAt ?? empty.lastRefreshedAt,
  };
}
